import { Box, Stack } from '@mui/material';
import { Thread, useGetThreadsQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { SearchInput } from '@schooly/components/filters';
import {
  EmptyThreadsStub,
  ThreadItem,
  ThreadParams,
  ThreadSkeletonRows,
  ThreadsUnread,
  useThreads,
} from '@schooly/components/threads';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const SKELETON_COUNT = 6;
export const DEFAULT_THREADS_PAGE_SIZE = 20;

export type MessageThreadListProps = {
  onThreadClick: (thread: Thread) => void;
} & ThreadParams;

export const MessageThreadList: FC<MessageThreadListProps> = ({
  schoolId,
  rootEntityType,
  rootEntityId,
  onThreadClick,
}) => {
  const { selectedThread } = useThreads();
  const { formatMessage } = useIntl();
  const {
    data: threads,
    setParams,
    params,
    isLoading,
    isInitialLoading,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetThreadsQuery(
    {
      school_id: schoolId,
      root_entity_type: rootEntityType,
      root_entity_id: rootEntityId,
      page_size: DEFAULT_THREADS_PAGE_SIZE,
    },
    { refetchOnMount: 'always' },
  );

  const handleSetFiltersQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleSetFiltersUnread = useCallback(() => {
    setParams((p) => ({ ...p, unread: !p.unread }));
  }, [setParams]);

  const entities = useMemo(() => threads?.pages.flatMap((page) => page.results) ?? [], [threads]);
  const loaderRef = useInfiniteScroll(isFetching, fetchNextPage, hasNextPage);
  const total = threads?.pages[0]?.count;

  const isThreadsFetching = (isLoading || isFetching) && !isFetchingNextPage;
  const isFirstFetching = isThreadsFetching && !isFetchingNextPage;

  return (
    <Stack height="100%">
      <Stack px={1.5} pt={1} pb={0.5} gap={0.5}>
        <SearchInput
          value={params.query || ''}
          onChangeText={handleSetFiltersQuery}
          placeholder={formatMessage({ id: 'messages-SearchAmongRecipients' })}
        />

        <ThreadsUnread
          isLoading={isInitialLoading && !entities.length}
          disabled={isFetching || isLoading}
          showUnread={!!params?.unread}
          onToggleUnread={handleSetFiltersUnread}
        />
      </Stack>

      <Box px={1.5} overflow="auto">
        {isFirstFetching || !threads ? (
          <ThreadSkeletonRows rowCount={SKELETON_COUNT} />
        ) : (
          <>
            {entities.map((thread) => {
              return (
                <MessageThreadListItem
                  key={thread.id}
                  thread={thread}
                  selectedThread={selectedThread}
                  onThreadClick={onThreadClick}
                />
              );
            })}
            {isThreadsFetching && !isFetchingNextPage && (
              <ThreadSkeletonRows rowCount={SKELETON_COUNT} />
            )}
            {!entities.length && !isThreadsFetching && (
              <EmptyThreadsStub unread={!!params?.unread} />
            )}

            {hasNextPage && (
              <>
                <div ref={loaderRef} />
                <ThreadSkeletonRows
                  rowCount={Math.min(
                    SKELETON_COUNT,
                    total && threads
                      ? total - threads.pages.length * SKELETON_COUNT
                      : SKELETON_COUNT,
                  )}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

type MessageThreadListItemProps = {
  thread: Thread;
  selectedThread?: Thread;
  onThreadClick: (thread: Thread) => void;
};

const MessageThreadListItem = ({
  thread,
  selectedThread,
  onThreadClick,
}: MessageThreadListItemProps) => {
  const [updatedThread, setUpdatedThread] = useState<Thread | null>(null);
  const currentThread = updatedThread ?? thread;
  const { currentUser } = useAuth();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    if (selectedThread?.channel && selectedThread.id === thread.id) {
      setUpdatedThread(selectedThread);
    }
  }, [currentThread, selectedThread, thread.id]);

  return (
    <ThreadItem
      thread={currentThread}
      onClick={() => {
        if (thread.id === selectedThread?.id) return;
        onThreadClick(currentThread);
      }}
      userId={currentUser?.user_id ?? ''}
    />
  );
};
